<div class="page-container">

  <!-- Page Header -->
  <div class="page-header">
    <mat-icon class="page-icon">public</mat-icon>
    <h1 class="page-title">Global variables</h1>
  </div>

  <!-- Tabs -->
  <mat-tab-group animationDuration="0ms" class="gv-tabs">

    <!-- ===== GENERAL TAB ===== -->
    <mat-tab label="GENERAL">
      <div class="tab-content">
        <form [formGroup]="form" autocomplete="off">

          <!-- Three-column identity / address / web section -->
          <div class="three-col-grid">

            <!-- Left column -->
            <div class="form-col">
              <!-- Platform main logo -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Platform main logo image</mat-label>
                <input matInput [value]="imageLabel('platformLogo')" readonly aria-label="Platform main logo image" />
                <button mat-icon-button matSuffix type="button" (click)="platformLogoFile.click()" aria-label="Upload Platform main logo image">
                  <mat-icon class="attach-icon">attach_file</mat-icon>
                </button>
              </mat-form-field>
              <input #platformLogoFile type="file" accept="image/png,image/jpeg" hidden (change)="onImagePicked($event, 'platformLogo')" />
              @if (isDataImage('platformLogo')) {
                <img class="image-preview" [src]="form.get('platformLogo')?.value" alt="Platform main logo image preview" />
              }

              <!-- County name -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>County name</mat-label>
                <input matInput formControlName="countyName" aria-label="County name" />
              </mat-form-field>

              <!-- Tax Collector's Name -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Tax Collector's Name</mat-label>
                <input matInput formControlName="taxCollectorName" aria-label="Tax Collector's Name" />
              </mat-form-field>

              <!-- Tax Collector's phone -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Tax Collector's phone number</mat-label>
                <input matInput formControlName="taxCollectorPhone" aria-label="Tax Collector's phone number" />
              </mat-form-field>

              <!-- Official Signature image -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Official Signature image</mat-label>
                <input matInput [value]="imageLabel('signatureImage')" readonly aria-label="Official Signature image" />
                <button mat-icon-button matSuffix type="button" (click)="signatureImageFile.click()" aria-label="Upload Official Signature image">
                  <mat-icon class="attach-icon">attach_file</mat-icon>
                </button>
              </mat-form-field>
              <input #signatureImageFile type="file" accept="image/png,image/jpeg" hidden (change)="onImagePicked($event, 'signatureImage')" />
              @if (isDataImage('signatureImage')) {
                <img class="image-preview" [src]="form.get('signatureImage')?.value" alt="Official Signature image preview" />
              }
            </div>

            <!-- Middle column -->
            <div class="form-col">
              <!-- Street address -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Tax Collector's street address</mat-label>
                <input matInput formControlName="streetAddress" aria-label="Street address" />
              </mat-form-field>

              <!-- Apt / Unit / Suite -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Apt/Unit/Suite</mat-label>
                <input matInput formControlName="aptSuite" aria-label="Apt, Unit or Suite" />
              </mat-form-field>

              <!-- City / State / Zip row -->
              <div class="city-state-zip-row">
                <mat-form-field appearance="outline" class="city-field">
                  <mat-label>City</mat-label>
                  <input matInput formControlName="city" aria-label="City" />
                </mat-form-field>

                <mat-form-field appearance="outline" class="state-field">
                  <mat-label>State</mat-label>
                  <mat-select formControlName="state" aria-label="State">
                    @for (state of usStates; track state) {
                      <mat-option [value]="state">{{ state }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline" class="zip-field">
                  <mat-label>Zip code</mat-label>
                  <input matInput formControlName="zipCode" aria-label="Zip code" />
                </mat-form-field>
              </div>
            </div>

            <!-- Right column -->
            <div class="form-col">
              <!-- Website URL -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Tax Collector's Website URL</mat-label>
                <input matInput formControlName="websiteUrl" aria-label="Tax Collector's Website URL" />
              </mat-form-field>

              <!-- Property Appraiser URL -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Property Appraiser's Website URL</mat-label>
                <input matInput formControlName="appraiserUrl" aria-label="Property Appraiser's Website URL" />
              </mat-form-field>

              <!-- Tourist Tax Group Email -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Tourist Tax Group Email Address</mat-label>
                <input matInput formControlName="taxGroupEmail" aria-label="Tourist Tax Group Email Address" />
              </mat-form-field>
            </div>
          </div>

          <!-- Payment threshold section -->
          <div class="section-header">
            <mat-icon class="section-icon">sync</mat-icon>
            <span class="section-label">Payment threshold</span>
          </div>

          <div class="two-col-grid payment-threshold-row">
            <mat-form-field appearance="outline">
              <mat-label>Under</mat-label>
              <span matPrefix>$&nbsp;</span>
              <input matInput type="number" formControlName="paymentUnder" aria-label="Payment threshold under" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Over</mat-label>
              <span matPrefix>$&nbsp;</span>
              <input matInput type="number" formControlName="paymentOver" aria-label="Payment threshold over" />
            </mat-form-field>
          </div>

          <!-- Four-column operational row -->
          <div class="four-col-grid operational-row">
            <mat-form-field appearance="outline">
              <mat-label>Address review cycle</mat-label>
              <input matInput type="number" formControlName="addressReviewCycle" aria-label="Address review cycle in days" />
              <span matSuffix class="days-suffix">Days</span>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Elapsed days for voids</mat-label>
              <input matInput type="number" formControlName="elapsedDaysVoids" aria-label="Elapsed days for voids" />
              <span matSuffix class="days-suffix">Day</span>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Annual historical data purge alert</mat-label>
              <input
                matInput
                [matDatepicker]="purgePicker"
                formControlName="annualPurgeAlert"
                aria-label="Annual historical data purge alert date"
              />
              <mat-datepicker-toggle matSuffix [for]="purgePicker"></mat-datepicker-toggle>
              <mat-datepicker #purgePicker></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Generate next year holidays</mat-label>
              <input
                matInput
                [matDatepicker]="holidayPicker"
                formControlName="generateHolidays"
                aria-label="Generate next year holidays date"
              />
              <mat-datepicker-toggle matSuffix [for]="holidayPicker"></mat-datepicker-toggle>
              <mat-datepicker #holidayPicker></mat-datepicker>
            </mat-form-field>
          </div>

          <!-- Email variables section -->
          <div class="section-header">
            <mat-icon class="section-icon">email</mat-icon>
            <span class="section-label">Email variables</span>
          </div>

          <div class="three-col-grid email-vars-row">
            <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
              <mat-label>General document image</mat-label>
              <input matInput formControlName="generalDocImage" readonly aria-label="General document image" />
              <mat-icon matSuffix class="attach-icon">attach_file</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
              <mat-label>No reply email</mat-label>
              <input matInput formControlName="noReplyEmail" aria-label="No reply email" />
              <mat-hint>Address outgoing mail is sent from. It must be verified in AWS SES,
                otherwise the system keeps using its configured sender.</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
              <mat-label>Reply email</mat-label>
              <input matInput formControlName="replyEmail" aria-label="Reply email" />
              <mat-hint>Where replies go. Leave blank to send replies to the address above.</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
              <mat-label>Staff alert email</mat-label>
              <input matInput formControlName="staffAlertEmail" aria-label="Staff alert email" />
              <mat-hint>Shared mailbox told about new registrations and added properties awaiting approval. Leave blank for no alerts.</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
              <mat-label>Generate returns when billing is activated</mat-label>
              <mat-select formControlName="autoGenerateOnActivation" aria-label="Generate returns when billing is activated">
                <mat-option value="true">Yes</mat-option>
                <mat-option value="false">No</mat-option>
              </mat-select>
              <mat-hint>Yes: missing returns are created as soon as an account's billing is set to Active. No: they are created in the nightly run.</mat-hint>
            </mat-form-field>
          </div>

        </form>
      </div>
    </mat-tab>

    <!-- ===== CERTIFICATES AND LETTERS TAB ===== -->
    <mat-tab label="CERTIFICATES AND LETTERS">
      <div class="tab-content">
        <form [formGroup]="form" autocomplete="off">
          <div class="cert-form">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>County name</mat-label>
              <input matInput formControlName="certCountyName" aria-label="County name for certificates" />
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>County tax collector name</mat-label>
              <input matInput formControlName="certTaxCollectorName" aria-label="County tax collector name for certificates" />
            </mat-form-field>
          </div>
        </form>
      </div>
    </mat-tab>

  </mat-tab-group>

  <!-- Footer Actions -->
  <div class="footer-actions">
    <button mat-stroked-button class="go-back-btn" type="button" (click)="goBack()" aria-label="Go back">
      GO BACK
    </button>
    <button mat-flat-button color="primary" class="save-btn" type="button" (click)="onSave()" aria-label="Save global variables">
      SAVE
    </button>
  </div>

</div>
